import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
// Make sure analytics and logEvent are correctly imported and initialized
import { getAnalytics, logEvent } from "firebase/analytics"; // Example import

// Initialize analytics if not done elsewhere globally
const analytics = getAnalytics(); // Example initialization

function StripePaymentReturn() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation("StripePaymentReturn"); // Ensure namespace exists

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    // Optional: statusParam might be useful for initial filtering but session check is primary
    // const statusParam = searchParams.get("status");

    const fetchSessionStatus = async () => {
      setLoading(true); // Start loading

      if (!sessionId) {
        setStatus("error");
        setMessage(t("missingSessionId")); // Add translation key
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          // Ensure your proxy or environment variable points correctly to your backend
          `/api/StripeSessionStatus?session_id=${sessionId}`
        );

        // Check if the response is ok before trying to parse JSON
        if (!response.ok) {
            const errorText = await response.text(); // Get error text if possible
             throw new Error(`Failed to fetch session status (${response.status}): ${errorText}`);
        }

        const session = await response.json();
        console.debug("Stripe Session Data:", session); // Log fetched session

        // --- Process based on session status ---
        if (session.status === "open") {
          setMessage(t("paymentSessionOpen"));
          setStatus("pending");
        } else if (session.status === "complete") {
          setStatus("success");
          setMessage(t("paymentSuccess"));

          // --- Build Line Items for Analytics from Metadata ---
          const lineItemsForAnalytics = [];
          const metadata = session.metadata || {}; // Use empty object if metadata is null/undefined
          const discountPercent = parseFloat(metadata.discountPercentApplied || 0); // Use discount from metadata

           // Helper to safely parse float from metadata string
          const parseMetaFloat = (key) => parseFloat(metadata[key] || 0);
           // Helper to safely parse int from metadata string
          const parseMetaInt = (key) => parseInt(metadata[key] || 0, 10);


          // --- Basic ---
          const basicQty = parseMetaInt('basicLicenses');
          if (basicQty > 0) {
            const unitPrice = parseMetaFloat('basicPriceUsed');
            const priceAfterDiscount = unitPrice * (1 - discountPercent / 100);
            lineItemsForAnalytics.push({
              item_id: "PRORECRUIT_LICENSE_BASIC",
              item_name: "ProRecruit Basic License",
              unit_price: unitPrice, // Price BEFORE discount
              price: priceAfterDiscount, // Price AFTER discount per unit
              quantity: basicQty,
              type: "Basic", // For mapping category4
              discount_per_unit: unitPrice * (discountPercent / 100),
            });
          }

          // --- Growth ---
          const growthQty = parseMetaInt('growthLicenses');
          if (growthQty > 0) {
            const unitPrice = parseMetaFloat('growthPriceUsed');
            const priceAfterDiscount = unitPrice * (1 - discountPercent / 100);
             lineItemsForAnalytics.push({
               item_id: "PRORECRUIT_LICENSE_GROWTH", // New ID
               item_name: "ProRecruit Growth License", // New Name
               unit_price: unitPrice,
               price: priceAfterDiscount,
               quantity: growthQty,
               type: "Growth", // New Type
               discount_per_unit: unitPrice * (discountPercent / 100),
             });
          }

          // --- Pro ---
          const proQty = parseMetaInt('proLicenses');
           if (proQty > 0) {
            const unitPrice = parseMetaFloat('proPriceUsed');
            const priceAfterDiscount = unitPrice * (1 - discountPercent / 100);
             lineItemsForAnalytics.push({
               item_id: "PRORECRUIT_LICENSE_PRO", // Consistent ID
               item_name: "ProRecruit Professional License", // Consistent Name
               unit_price: unitPrice,
               price: priceAfterDiscount,
               quantity: proQty,
               type: "Professional", // Use full name for consistency
               discount_per_unit: unitPrice * (discountPercent / 100),
             });
           }

          // --- Premium ---
          const premiumQty = parseMetaInt('premiumLicenses');
           if (premiumQty > 0) {
            const unitPrice = parseMetaFloat('premiumPriceUsed');
            const priceAfterDiscount = unitPrice * (1 - discountPercent / 100);
             lineItemsForAnalytics.push({
               item_id: "PRORECRUIT_LICENSE_PREMIUM", // New ID
               item_name: "ProRecruit Premium License", // New Name
               unit_price: unitPrice,
               price: priceAfterDiscount,
               quantity: premiumQty,
               type: "Premium", // New Type
               discount_per_unit: unitPrice * (discountPercent / 100),
             });
           }

           console.debug("Items prepared for Analytics:", lineItemsForAnalytics);

          // --- Log Purchase Event ---
          // Convert amount_total from cents if currency is not zero-decimal (like XOF)
          let finalValue = session.amount_total;
          const currency = session.currency.toLowerCase();
          // List of zero-decimal currencies (expand if needed) https://stripe.com/docs/currencies#zero-decimal
          const zeroDecimalCurrencies = ['bif', 'clp', 'djf', 'gnf', 'jpy', 'kmf', 'krw', 'mgf', 'pyg', 'rwf', 'ugx', 'vnd', 'vuv', 'xaf', 'xof', 'xpf'];
          if (!zeroDecimalCurrencies.includes(currency)) {
            finalValue = session.amount_total / 100;
          }

          logEvent(analytics, "purchase", {
            currency: currency.toUpperCase(), // GA4 expects uppercase
            value: finalValue, // Use converted value
            transaction_id: sessionId, // Use session ID as transaction ID
            coupon: discountPercent > 0 ? `DISCOUNT_${discountPercent}` : undefined, // Example coupon logging
            items: lineItemsForAnalytics.map((item, index) => ({
              item_id: item.item_id,
              item_name: item.item_name,
              affiliation: "ProRecruit",
              coupon: discountPercent > 0 ? `DISCOUNT_${discountPercent}` : undefined, // Apply same coupon logic per item if desired
              discount: item.discount_per_unit * item.quantity, // Total discount FOR THIS ITEM LINE
              index: index + 1,
              item_brand: "ProRecruit",
              item_category: "Software License",
              item_category2: "Recruitment",
              item_category3: "License",
              item_category4: item.type, // Use the type added earlier
              // item_category5: "", // Add if needed
              // item_list_id: "checkout_page", // Optional: Source list
              // item_list_name: "Checkout Page", // Optional: Source list name
              // item_variant: "", // Optional: if applicable
              price: item.price, // Price PER UNIT after discount
              quantity: item.quantity,
            })),
            // Optionally include other parameters like shipping, tax if applicable
            // shipping: 0,
            // tax: 0,
          });
           console.info(`Purchase event logged for session ${sessionId}`);

        } else if (session.status === "expired") {
          setStatus("error"); // Treat expired as an error/failed state for the user
          setMessage(t("paymentSessionExpired")); // Add translation key
        } else {
           // Handle other unexpected statuses if necessary
           setStatus("error");
           setMessage(
             `${t("unexpectedSessionStatus")} ${session.status}` // Add translation key
           );
           console.warn(`Unexpected Stripe session status: ${session.status}`, session);
        }
      } catch (error) {
        setStatus("error");
        setMessage(`${t("paymentError")}: ${error.message}`);
        console.error("Error processing payment return:", error);
        logEvent(analytics, "payment_return_error", {
          error_message: error.message,
          session_id: sessionId || "unknown",
        });
      } finally {
        setLoading(false); // Stop loading regardless of outcome
      }
    };

    fetchSessionStatus();
  }, [searchParams, t]); // Dependency array - t is needed if translations change dynamically

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
        <p className="ml-4 text-xl">Verifying Payment...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 py-12 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-lg shadow-xl">
        <div className="text-center">
          {/* Dynamic Icon based on status */}
          {status === "success" && (
             <svg className="mx-auto h-12 w-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
             </svg>
          )}
           {(status === "error" || status === "failed") && (
             <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
             </svg>
           )}
            {status === "pending" && (
             <svg className="mx-auto h-12 w-12 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.79 4 4s-1.79 4-4 4c-1.742 0-3.223-.835-3.772-2M12 6V4m0 16v-2" />
             </svg>
           )}

          <h1 className="mt-3 text-center text-3xl font-extrabold text-slate-900 font-bold tracking-tight">
            {t("paymentStatus")}
          </h1>

          {/* Message Display */}
          <div className={`mt-5 p-4 rounded-md border ${
              status === "success" ? "bg-green-50 border-green-300 text-green-800" :
              status === "pending" ? "bg-yellow-50 border-yellow-300 text-yellow-800" :
              "bg-red-50 border-red-300 text-red-800" // Covers failed and error
            }`}
             role="alert"
           >
            <p className="font-medium">
               { status === 'success' ? t('successTitle', 'Success!') :
                 status === 'pending' ? t('pendingTitle', 'Pending') :
                 t('errorTitle', 'Error') // Default for failed/error
               } {/* Add translation keys */}
            </p>
            <p className="mt-1 text-sm">{message || t('defaultMessage', 'Processing your payment status.')}</p> {/* Add translation key */}
          </div>


          <div className="mt-8 flex justify-center gap-4">
            <Link
              to="/dashboard" // Adjust link as needed
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {t("goToDashboard")}
            </Link>
             {/* Optionally show Back to Checkout only if payment failed/pending */}
            {(status === 'error' || status === 'failed' || status === 'pending') && (
                <Link
                to="/checkout" // Adjust link as needed
                className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md shadow-sm text-slate-600 leading-relaxed bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                {t("backToCheckout")}
                </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default StripePaymentReturn;